import React, { memo, useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { useUser } from "../../../../context/User.context";
import ThemeSchema from "../../../../theme/schema";
import {
  ActionStatusType,
  fetchActionErrorStats,
} from "../../../../BytebeamClient";
import LoadingAnimation from "../../../common/Loader";

interface ErrorDistributionChartProps {
  height: number;
  width: number;
  action: ActionStatusType;
}

function ErrorDistributionChart(props: ErrorDistributionChartProps) {
  const { action } = props;

  const { user } = useUser();
  const theme = user?.settings?.theme;

  const [errorStatsData, setErrorStatsData] = useState<Record<string, number>>(
    {}
  );
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = React.useState(0);

  function getTotalCount(data: Record<string, number>) {
    return Object.values(data).reduce(
      (acc: number, curr: number) => acc + curr,
      0
    );
  }

  const totalDevices = Object.values(errorStatsData).reduce(
    (sum, value) => sum + value,
    0
  );

  useEffect(() => {
    let pollingTimeout: NodeJS.Timeout;
    const abortController = new AbortController();
    let isMounted = true;
    let isLoading = true;

    async function fetchErrorStats() {
      if (!isMounted) return;

      try {
        if (isLoading) setLoading(isLoading);
        const res: Record<string, number> = await fetchActionErrorStats(
          action.action_id,
          abortController.signal
        );

        if (isMounted) {
          setErrorStatsData(res);
          setTotalCount(getTotalCount(res));
        }
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("Error fetching stats:", error);
        }
      } finally {
        if (isLoading) {
          setLoading(false);
          isLoading = false;
        }
        if (isMounted) {
          pollingTimeout = setTimeout(() => {
            fetchErrorStats();
          }, 10 * 1000); // Polling every 10 seconds
        }
      }
    }

    fetchErrorStats();

    return () => {
      isMounted = false;
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
      abortController.abort();
    };
  }, [action.action_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <LoadingAnimation
        loaderSize="28px"
        fontSize="18px"
        loaderBorderSize="3px"
        marginTopText="6px"
        loadingText="Loading error distribution..."
      />
    );
  } else if (totalCount === 0) {
    return <div>No Error Found</div>;
  } else {
    return (
      <div>
        <Plot
          data={[
            {
              name: "Actions Summary",
              values: Object.values(errorStatsData) ?? [],
              labels:
                Object.keys(errorStatsData)?.map((key) =>
                  key?.length > 150 ? `${key?.slice(0, 150)}...` : key
                ) ?? [],
              marker: {
                showscale: false,
                colors:
                  ThemeSchema.data[theme ?? "dark"]?.colors[
                    "error-stats-charts-color-scheme-array"
                  ],
              },
              type: "pie",
              showlegend: false,
              automargin: false,
              text: [...Object.entries(errorStatsData)].map(
                ([key, value]) => `${key} - ${value}`
              ),
              textinfo: "value",
              textposition: "inside",
              hole: 0.6,
              hovertemplate: "<b>%{label}</b><br>%{value} <extra></extra>",
              hoverlabel: {
                bordercolor:
                  ThemeSchema.data[theme ?? "dark"]?.colors[
                    "bars-inner-text-color"
                  ],
                font: {
                  color:
                    ThemeSchema.data[theme ?? "dark"]?.colors[
                      "foreground-color"
                    ],
                  family: "Open Sans",
                  size: 12,
                },
                bgcolor:
                  ThemeSchema.data[theme ?? "dark"]?.colors["background-color"],
                align: "left",
              },
              pull: Array(Object.keys(errorStatsData)?.length)?.fill(0.01),
            },
          ]}
          layout={{
            height: props.height,
            width: props.width,
            margin: {
              l: 0,
              r: 0,
              b: 20,
              t: 20,
            },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            annotations: [
              {
                x: 0.5,
                y: 0.5,
                xref: "paper",
                yref: "paper",
                text:
                  totalDevices === 1 ? "1 device" : `${totalDevices} devices`,
                showarrow: false,
                font: {
                  size: 16,
                  family: "Open Sans, sans-serif",
                  color:
                    ThemeSchema.data[theme ?? "dark"]?.colors[
                      "foreground-color"
                    ],
                },
                align: "center",
              },
            ],
          }}
          config={{
            displayModeBar: false,
          }}
        />
      </div>
    );
  }
}

export default memo(ErrorDistributionChart);
